<script lang="ts" setup>
import concat from 'lodash-es/concat';
import lodashFilter from 'lodash-es/filter';
import debounce from 'lodash-es/debounce';
import DropZone from '../ideo/form-file/DropZone.vue';
import type { MovedResources, FolderPermissions, Scope } from '../../runtime/services/StorageService';
import StorageService from '../../runtime/services/StorageService';
import MenuList from '../../layouts/_partials/default/navigation/MenuList.vue';
import MenuFilters from '../../pages/trash/_partials/MenuFilters.vue';
import CCropper from './Cropper.vue';
import CResourceList from './resource/ResourceList.vue';
import CResourceInfo from './resource/ResourceInfo.vue';
import CNewFolderModal from './modals/NewFolderModal.vue';
import CDeleteModal from './modals/DeleteModal.vue';
import CMoveModal from './modals/MoveModal.vue';
import CRenameModal from './modals/RenameModal.vue';
import CResizeModal from './modals/ResizeModal.vue';
import CFoldersTree from './FoldersTree.vue';
import CUpload from './Upload.vue';

const { $log } = useLogging();
const { $t } = useLocalization();
const { $alert } = useAlerts();
const { $events } = useEvents();

const { cursor, cursorDestination, onClick } = useShiftSelect();
const { filemanagerData } = useFilemanagerStore();

const props = withDefaults(defineProps<{
    value?: any;
    modal?: boolean;
    media?: string;
    maxFiles?: number;
}>(), {
    value: [],
    media: 'files',
    maxFiles: 1
});

provide('modal', props.modal);

const sidebar = ref(false);
const toolbarPosition = computed<string>(() => props.modal ? 'header' : 'toolbar');

const template = ref<'thumbs' | 'big-thumbs' | 'list'>('big-thumbs');
const resources = ref([]) as Ref<FileResource[]>;
const selected = ref<FileResource[]>([]);
const permissions = ref({
    canView: false,
    canEdit: false,
    canDelete: false,
    canManage: false
}) as Ref<FolderPermissions>;
const filters = ref({ search: '', dateFromUtc: '', dateDueUtc: '' });
const realTimeFolder = ref<number>(null);
const everywhere = ref(false);
const pager = ref(new Pager(1, 50, 'Name', 'ASC')) as Ref<Pager>;
const message = ref<string>(null);
const checkAll = ref(false);

const mouseClick = useMouseClick();
// ------------------

// Scopes
interface ScopeOption
{
    id: Scope;
    label: any;
    icon: string;
    name: string;
}

const scopes = computed<ScopeOption[]>(() => ([
    { id: 'public', name: $t('[[[Pliki współdzielone]]]'), label: $t('[[[Pliki współdzielone]]]'), icon: 'far fa-lock-open' },
    { id: 'private', name: $t('[[[Moje pliki]]]'), label: $t('[[[Moje pliki]]]'), icon: 'far fa-lock' },
    { id: 'selected', name: $t('[[[Wybrane pliki]]]'), label: $t('[[[Wybrane pliki]]]'), icon: 'far fa-check-square', badge: selected.value.length }
]));
const scopesRoot = computed(() => ({
    name: 'scopes',
    children: scopes.value as any
}));

const changeScope = async ({ id }: ScopeOption): Promise<void> =>
{
    cursor.value = 0;
    cursorDestination.value = -1;
    filemanagerData.scope = id;
    filemanagerData.folder = null;
    pager.value.setPageIndex(1);
    resources.value = [];
    await loadData();
};

const sortingOptions: Option[] = [
    { value: 'Name', text: '[[[Nazwa]]]' },
    { value: 'Size', text: '[[[Rozmiar]]]' },
    { value: 'DateCreatedUtc', text: '[[[Data dodania]]]' },
    { value: 'DateModifiedUtc', text: '[[[Data modyfikacji]]]' }
];

const getCurrentScope = (): ScopeOption =>
{
    return scopes.value.find(p => p.id == filemanagerData.scope);
};
// ------------------

const folders = computed({
    get(): FileResource[]
    {
        return (filemanagerData.scope === 'selected' ? selected : resources).value.filter(el => el.resourceType === 'folder');
    },
    set(value: FileResource[])
    {
        if (filemanagerData.scope === 'selected')
            selected.value = [...value, ...selected.value.filter(el => el.resourceType !== 'folder')];
    }
});

const files = computed({
    get(): FileResource[]
    {
        return (filemanagerData.scope === 'selected' ? selected : resources).value.filter(el => el.resourceType === 'file');
    },
    set(value: FileResource[])
    {
        if (filemanagerData.scope === 'selected')
            selected.value = [...value, ...selected.value.filter(el => el.resourceType !== 'file')];
    }
});

const getResources = async (s: Scope = null, publicIds: string[] = undefined): Promise<any> =>
{
    const { search, dateFromUtc, dateDueUtc } = filters.value;

    return await StorageService.getResources(s || filemanagerData.scope, filemanagerData.folder, search, dateFromUtc, dateDueUtc, everywhere.value, pager.value, publicIds);
};

const loading = ref(false);
const loadData = async (append: boolean = false): Promise<void> =>
{
    try
    {
        loading.value = true;

        if (filemanagerData.scope !== 'selected')
        {
            const {
                totalRows,
                breadcrumbs,
                meta,
                message: messageText,
                items
            } = await getResources();

            pager.value.setTotalRows(totalRows);
            setBreadcrumbs(breadcrumbs);
            permissions.value = meta;
            message.value = messageText;

            resources.value = (append ? concat(resources.value, items) : items) as FileResource[];

            await foldersTreeRef.value.reload();
        }
        else
        {
            pager.value.setTotalRows(selected.value.length);
            setBreadcrumbs([]);
            permissions.value = {
                canView: true,
                canEdit: true,
                canDelete: true,
                canManage: true
            };
            message.value = null;
        }
    }
    catch (ex)
    {
        pager.value.setTotalRows(0);
        resources.value = [];
        $log.debug(ex);
    }
    finally
    {
        realTimeFolder.value = filemanagerData.folder;
        loading.value = false;
    }
};

const loadMore = (): void =>
{
    pager.value.setPageIndex(pager.value.getPageIndex() + 1);
    loadData(true);
};

onBeforeMount(async (): Promise<void> =>
{
    pager.value.setTotalRows(0);

    await loadData();

    if (props.value.includes('#(storage):'))
    {
        const url = props.value.split(':')[1];

        const { items } = await getResources(filemanagerData.scope === 'selected' ? 'public' : filemanagerData.scope, [url]);

        selected.value = items;
    }
    else if (props.value.includes('#(files):'))
    {
        const files = props.value.split(':')[1].split(',');

        const { items } = await getResources(filemanagerData.scope === 'selected' ? 'public' : filemanagerData.scope, files);

        selected.value = items.sort((a: any, b: any) => files.indexOf(a.publicId) - files.indexOf(b.publicId));
    }
    else
    {
        selected.value = props.value;
    }
});

const search = debounce(() =>
{
    if (filemanagerData.scope === 'selected')
        return;

    checked.value = null;
    pager.value.setPageIndex(1);
    loadData();
}, 500);

// Select all
const selectAllRef = ref<any>(null);

const selectAll = (): void =>
{
    const len = selected.value.length;

    if (filemanagerData.scope === 'selected')
    {
        selected.value = [];
        checked.value = null;

        return;
    }

    resources.value.forEach((el: FileResource) =>
    {
        if (len === 0 || len >= resources.value.length)
            selectItem(el, true);
        else
            selectItem(el, true, true);
    });
};

useShortcuts({
    ctrlA: selectAll
});

// ------------------

// Breadcrumbs
export interface Breadcrumb
{
    id: number;
    label: string;
    separator?: boolean;
}

const breadcrumbs = ref<Breadcrumb[]>([]);
const setBreadcrumbs = (arr: any[]): void =>
{
    breadcrumbs.value = [];

    arr.map(({ id, name }) =>
    {
        breadcrumbs.value.push({ id, label: name });
    });
};

const visibleBreadcrumbs = computed(() =>
{
    if (breadcrumbs.value?.length < 4)
        return breadcrumbs.value;
    else
        return [breadcrumbs.value[0], { id: null, label: '...', separator: true }, ...breadcrumbs.value.slice(-2)];
});
// ------------------

const treeDestination = ref<number>(null);
const expanded = ref([0]);

const changeFolder = async (s: string, f: number): Promise<void> =>
{
    if ((filemanagerData.scope === s && filemanagerData.folder === f) || filemanagerData.scope === 'selected')
        return;

    if (s)
        filemanagerData.scope = s as Scope;

    filemanagerData.folder = f;
    treeDestination.value = f;

    checkAll.value = false;
    checked.value = null;
    pager.value.setPageIndex(1);

    await loadData();
};

watch(treeDestination, folderId => changeFolder(filemanagerData.scope, folderId));

const selectItem = (item: FileResource, ctrlKey?: boolean, preventRemove?: boolean, e?: MouseEvent): void =>
{
    const ids = resources.value.map(el => el.id);

    if (e && e.shiftKey && filemanagerData.scope !== 'selected')
    {
        const selectedIds = onClick(selected.value.map(el => el.id), ids, item.id, e);

        selected.value = resources.value.filter(obj => selectedIds.includes(obj.id));
        checked.value = selected.value.length === 1 ? selected.value[0] : null;

        return;
    }

    cursor.value = ids.indexOf(item.id);
    cursorDestination.value = -1;

    const index = selected.value.findIndex?.((el: FileResource) => el.id === item.id && el.resourceType === item.resourceType);

    if (filemanagerData.scope !== 'selected')
    {
        if (ctrlKey)
        {
            if (index > -1 && !preventRemove)
                selected.value.splice(index, 1);
            else if (index === -1)
                selected.value.push(item);

            checked.value = selected.value.length === 1 ? selected.value[0] : null;
        }
        else
        {
            selected.value = [item];
            checked.value = item;
        }
    }
    else
    {
        if (ctrlKey && index > -1 && !preventRemove)
        {
            selected.value.splice(index, 1);
            checked.value = null;
        }
        else
        {
            checked.value = item;
        }
    }
};

const downloadItem = (item: FileResource): void =>
{
    window.location.assign(baseurl(item.url) + '?dl=true');
};

// Cropper
const cropper = ref(false);
const cropLoading = ref(false);
const cropForm = Form.create({
    width: 0,
    height: 0,
    x: 0,
    y: 0
});
const showCropperButton = computed(() =>
{
    if (!permissions.value.canEdit)
        return false;

    if (filemanagerData.scope === 'selected' && checked.value && checked.value.resourceType === 'file' && checked.value.isImage)
        return true;
    else if (!cropper.value && selected.value.length === 1 && selected.value[0].resourceType === 'file' && selected.value[0].isImage)
        return true;

    return false;
});
const showCropperToolbar = computed(() => cropper.value);

const currentEdit = ref<FileResource>(null);
const editItem = (item: FileResource): void =>
{
    currentEdit.value = item;
    cropper.value = true;
};

const cropImage = async (): Promise<void> =>
{
    try
    {
        cropLoading.value = true;

        await StorageService.cropImage(currentEdit.value.id, cropForm.data());

        $alert.success($t('[[[Obraz został wycięty.]]]'));
        await loadData();
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
    finally
    {
        cropLoading.value = false;
        cropper.value = false;
    }
};
// ------------------

// Toolbar actions
const showSelectedButton = computed(() =>
{
    const conditions: boolean[] = [!cropper.value, props.modal];

    if (props.maxFiles === 0)
        conditions.push(!!selected.value.length);
    else if (props.maxFiles === 1)
        conditions.push(checked.value && checked.value.resourceType === 'file');
    else
        conditions.push(selected.value.length <= props.maxFiles);

    return conditions.every(p => p);
});

const uniqueScopes = computed(() => [...new Set(selected.value.map(item => item.scope))]);

const showNewFolderButton = computed(() => !cropper.value && filemanagerData.scope !== 'selected' && permissions.value.canEdit);
const showDeleteButton = computed(() => !cropper.value && selected.value.length > 0 && permissions.value.canDelete);
const showMoveButton = computed(() => !cropper.value && selected.value.length > 0 && permissions.value.canDelete && uniqueScopes.value.length === 1);
const showRenameButton = computed(() => !cropper.value && selected.value.length === 1 && permissions.value.canEdit);
const showDownloadButton = computed(() => !cropper.value && selected.value.length === 1 && selected.value[0].resourceType === 'file');

const newFolderModalRef = ref<any>(null);
const deleteModalRef = ref<any>(null);
const moveModalRef = ref<any>(null);
const renameModalRef = ref<any>(null);
const resizeModalRef = ref<any>(null);
const foldersTreeRef = ref<any>(null);

provide('renameModalRef', renameModalRef);

const showButtons = computed(() => (
    showNewFolderButton.value ||
    showDeleteButton.value ||
    showMoveButton.value ||
    showRenameButton.value ||
    showDownloadButton.value ||
    showCropperButton.value ||
    showCropperToolbar.value
));

const deleted = async (elements: any[]): Promise<void> =>
{
    const index = elements.findIndex((el: any) =>
    {
        const isFolder = 'hasChildren' in el || el.resourceType === 'folder';

        if (isFolder && el.id === filemanagerData.folder)
            return true;

        return false;
    });

    if (index > -1)
    {
        filemanagerData.folder = elements[index]?.parentId;
        treeDestination.value = elements[index].parentId;
    }

    selected.value = [];
    checked.value = null;
    pager.value.setPageIndex(1);
    await loadData();
};

const moved = async (data: MovedResources): Promise<void> =>
{
    selected.value = lodashFilter(selected.value, p => (
        (p.resourceType === 'folder' && data.folders.indexOf(p.id) > -1) ||
        (p.resourceType === 'file' && data.files.indexOf(p.id) > -1)
    ));
    pager.value.setPageIndex(1);
    checked.value = null;
    await loadData();
};

const cropChanged = (params: any) =>
{
    cropForm.width = params.width;
    cropForm.height = params.height;
    cropForm.x = params.x;
    cropForm.y = params.y;
};
// ------------------

onMounted((): void =>
{
    $events.$on('filemanager::buttons::cropper', (val: boolean) => cropper.value = val);
    $events.$on('filemanager::cropper::changed', cropChanged);
});

onUnmounted((): void =>
{
    $events.$off('filemanager::buttons::cropper', (val: boolean) => cropper.value = val);
    $events.$off('filemanager::cropper::changed', cropChanged);
});

const closeModal = (): void =>
{
    $events.$emit('filemanager::modal::close', true);
};

const fileOptions: any = {
    'folder': { classes: 'fa-folder text-primary', text: $t('[[[Folder plików]]]') },
    'previous-folder': { classes: 'fa-folder text-primary', text: $t('[[[Folder plików]]]') },
    'image/jpg': { classes: 'fa-image text-info', text: $t('[[[Plik JPG]]]') },
    'image/jpeg': { classes: 'fa-image text-info', text: $t('[[[Plik JPEG]]]') },
    'image/png': { classes: 'fa-image text-info', text: $t('[[[Plik PNG]]]') },
    'image/gif': { classes: 'fa-file-gif text-info', text: $t('[[[Plik GIF]]]') },
    'text/plain': { classes: 'fa-file text-success', text: $t('[[[Dokument tekstowy]]]') },
    'text/css': { classes: 'fa-file-code text-danger', text: $t('[[[Plik CSS]]]') },
    'text/csv': { classes: 'fa-file-csv text-danger', text: $t('[[[Plik CSV]]]') },
    'text/html': { classes: 'fa-file-code text-danger', text: $t('[[[Plik HTML]]]') },
    'application/msword': { classes: 'fa-file-doc text-warning', text: $t('[[[Plik DOC]]]') },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { classes: 'fa-file-doc text-warning', text: $t('[[[Plik DOCX]]]') },
    'application/pdf': { classes: 'fa-file-pdf text-warning', text: $t('[[[Dokument PDF]]]') },
    'application/zip': { classes: 'fa-file-zip text-warning', text: $t('[[[Plik ZIP]]]') },
    'application/x-7z-compressed': { classes: 'fa-file-zipper text-warning', text: $t('[[[Plik skompresowany]]]') },
    'application/vnd.rar': { classes: 'fa-file-zipper text-warning', text: $t('[[[Plik skompresowany]]]') },
    'audio/mpeg': { classes: 'fa-file-audio', text: $t('[[[Plik audio]]]') },
    'video/mp4': { classes: 'fa-file-video', text: $t('[[[Plik MP4]]]') },
    'application/octet-stream': { classes: 'fa-file-binary', text: $t('[[[Plik binarny]]]') }
};

const getFileOptions = (item: FileResource): { classes: string; text: string } =>
{
    return fileOptions[item.contentType || item.resourceType] ?? { classes: 'fa-file', text: $t('[[[Nieznany typ pliku]]]') };
};

provide('getFileOptions', getFileOptions);

const isActive = ({ id, resourceType }: FileResource, source?: FileResource): boolean =>
{
    if (source !== undefined)
        return source?.id === id && source?.resourceType === resourceType;

    const index = selected.value.findIndex?.((el: FileResource) =>
    {
        return el.id === id && el.resourceType === resourceType;
    });

    return index >= 0;
};

const checked = ref<FileResource>(null);
const changeTemplate = (value: 'thumbs' | 'big-thumbs' | 'list'): void =>
{
    const redirectFrom = template.value;

    template.value = value;

    if (value !== 'list' && selected.value.length === 0 && checked.value)
        selected.value = [checked.value];
    else if (value === 'list' && selected.value.length === 1)
    {
        checked.value = selected.value[0];
    }

    if (redirectFrom === 'list' && selected.value.length > 1)
        checked.value = null;
};

const listRowClick = (item: FileResource, index: number, e: any): void =>
{
    if (item.resourceType === 'previous-folder')
        return;

    checked.value = item;

    mouseClick(
        () => (e?.ctrlKey || selected.value.length <= 1) && selectItem(item, e?.ctrlKey, false),
        () => item.resourceType === 'folder' && changeFolder(filemanagerData.scope, item.id)
    );
};

const getDropdownOptions = (item: FileResource): any[] =>
{
    if (!item)
        return [];

    const { resourceType, isImage } = item;

    if (resourceType === 'previous-folder')
    {
        return [
            { id: 'back', text: $t('[[[Powrót]]]'), icon: 'far fa-arrow-alt-circle-left', onClick: () => changeFolder(null, parentFolderId.value === -1 ? null : parentFolderId.value) }
        ];
    }

    return [
        { id: 'preview', inactive: !isImage, text: $t('[[[Podgląd]]]'), icon: 'far fa-eye' },
        { id: 'open', inactive: resourceType === 'file' || filemanagerData.scope === 'selected', text: $t('[[[Otwórz]]]'), icon: 'far fa-folder-open', onClick: () => changeFolder(filemanagerData.scope, item.id) },
        { id: 'select', inactive: isActive(item), text: $t('[[[Zaznacz]]]'), icon: 'far fa-square-dashed', onClick: () => selectItem(item, true) },
        { id: 'unselect', inactive: !isActive(item), text: $t('[[[Odznacz]]]'), icon: 'far fa-square-minus', onClick: () => selectItem(item, true) },
        { id: 'rename', text: $t('[[[Zmień nazwę]]]'), icon: 'far fa-pen', onClick: () => renameModalRef.value.showModal(item) },
        { id: 'edit', inactive: resourceType === 'folder' || !isImage, text: $t('[[[Edytuj]]]'), icon: 'far fa-pen-to-square', onClick: () => editItem(item) },
        { id: 'download', inactive: resourceType === 'folder', text: $t('[[[Pobierz]]]'), icon: 'far fa-download', onClick: () => downloadItem(item) },
        { id: 'delete', text: $t('[[[Usuń]]]'), icon: 'far fa-trash', onClick: () => deleteModalRef.value.showModal([item]) }
    ].filter(el => !el.inactive);
};

const bindItem = (option: any, resource: FileResource): any =>
{
    switch (option.id)
    {
        case 'preview':
            return {
                'data-src': baseurl(resource.url)
            };
        default:
            return null;
    }
};

provide('getDropdownOptions', getDropdownOptions);
provide('bindItem', bindItem);

const onImageError = (): void =>
{
    cropper.value = false;
};

const chooseSelected = (): void =>
{
    $events.$emit('filemanager::callback::value', selected.value);
};

// Upload and dropzone
const uploader = ref<any>(null);
const upload = ref<any>([]);

const onUpload = (): void =>
{
    $alert.success($t('[[[Przesyłanie plików powiodło się.]]]'));
    loadData();
};
// ------------------

const parentFolderId = computed(() =>
{
    if (breadcrumbs.value?.length)
    {
        const len = breadcrumbs.value.length;

        return breadcrumbs.value[len - 2]?.id ?? -1;
    }

    return null;
});

const prependFolderItems = computed(() =>
{
    const arr = [];

    if (breadcrumbs.value.length)
    {
        const backFolderClick = () =>
        {
            mouseClick(
                null,
                () =>
                {
                    changeFolder(null, parentFolderId.value === -1 ? null : parentFolderId.value);
                }
            );
        };

        arr.push({ resourceType: 'previous-folder', name: '..', id: parentFolderId.value, title: $t('[[[Powrót]]]'), onClick: backFolderClick, icon: 'fas fa-folder text-primary' });
    }

    return arr;
});

const getListViewItems = (items: FileResource[]): FileResource[] =>
{
    return [
        ...prependFolderItems.value,
        ...items
    ];
};
</script>

<template>
    <div class="filemanager" :class="[`filemanager-${template}`, checked && 'filemanager-details']">
        <!-- Menu -->
        <Portal :to="modal ? 'filemanager-modal-menu' : 'menu-section'">
            <MenuList
                :root="scopesRoot"
                :active="(node) => node.id === filemanagerData.scope"
                :toggle="changeScope"
                :children="(node) => node.children || []"
            />

            <MenuFilters
                v-model="filters"
                class="mt-3"
                @on-search="search"
                @on-reset-search="loadData"
                @on-change="loadData"
            />

            <div v-if="filemanagerData.scope !== 'selected'" class="filemanager-tree">
                <ActionButton
                    v-show="showNewFolderButton"
                    variant="light"
                    class="mb-2 w-100"
                    icon="far fa-folder"
                    :text="$t('[[[Dodaj nowy folder]]]')"
                    @click.prevent="newFolderModalRef.showModal()"
                />
                <CFoldersTree
                    ref="foldersTreeRef"
                    v-model:scope="filemanagerData.scope"
                    v-model:destination="treeDestination"
                    v-model:expanded="expanded"
                    @on-name-change="(item: any) => renameModalRef.showModal(item)"
                    @on-remove="(item: any) => deleteModalRef.showModal([item])"
                />
            </div>
        </Portal>

        <!-- Upload files -->
        <Portal to="upload-wrapper">
            <CUpload
                ref="uploader"
                v-model="upload"
                :scope="filemanagerData.scope"
                :folder="filemanagerData.folder"
                @reload="loadData"
                @send="onUpload"
                @cancel="upload = []"
            />
        </Portal>

        <!-- Modals -->
        <CNewFolderModal ref="newFolderModalRef" :scope="filemanagerData.scope" :folder="filemanagerData.folder" @reload="loadData" />
        <CDeleteModal ref="deleteModalRef" @deleted="deleted" />
        <CMoveModal ref="moveModalRef" :expanded="expanded" :scope="filemanagerData.scope" :destination="filemanagerData.folder" @moved="moved" />
        <CRenameModal ref="renameModalRef" @reload="loadData" />
        <CResizeModal ref="resizeModalRef" @reload="loadData" />

        <DataCard
            :use-panels="true"
            :open-right="sidebar"
            :disable-right="!(checked || upload.length) || cropper"
            :disable-left="!modal"
            :right-panel-name="$t('[[[Szczegóły]]]')"
            class="filemanager-data-card"
            sticky-panel-content
            @hide-right="sidebar = false"
        >
            <!-- Modal -->
            <template v-if="modal" #modal>
                <button class="btn btn-danger" :title="$t('[[[Zamknij]]]')" data-toggle="tooltip" @click.prevent="closeModal()">
                    <i class="far fa-times" />
                </button>
            </template>

            <!-- Right sidebar -->
            <template #right>
                <PortalTarget name="upload-wrapper" />
                <CResourceInfo
                    v-if="checked && !upload.length"
                    v-model="checked"
                    :scope="filemanagerData.scope"
                    :permissions="permissions"
                />
            </template>

            <template #left>
                <PortalTarget name="filemanager-modal-menu" />
            </template>

            <!-- Toolbar -->
            <template #[toolbarPosition]>
                <IdeoPanel :column="false" :stretch="false" :start="desktop">
                    <template #start>
                        <!-- Add new file -->
                        <ActionButton
                            v-show="filemanagerData.scope !== 'selected' && !modal"
                            variant="light"
                            :disabled="filemanagerData.scope === 'selected' || !permissions.canEdit"
                            icon="far fa-upload"
                            :text="$t('[[[Dodaj pliki]]]')"
                            @click.prevent="uploader.openDialog()"
                        />
                        <FilemanagerToolbar
                            ref="toolbarPlugin"
                            :cropper="cropper"
                            :permissions="permissions"
                            @reload="loadData()"
                        />
                        <!-- Toolbar actions -->
                        <ActionBar v-if="showButtons" :toggle-footer="true">
                            <ActionButton
                                v-show="showSelectedButton"
                                variant="success"
                                class="me-3"
                                icon="far fa-check"
                                :text="$t('[[[Wybierz zaznaczone]]]')"
                                @click.prevent="chooseSelected"
                            />
                            <ActionButton
                                v-show="filemanagerData.scope !== 'selected' && modal"
                                variant="primary me-auto me-xl-2"
                                :disabled="filemanagerData.scope === 'selected' || !permissions.canEdit"
                                icon="far fa-upload"
                                :text="$t('[[[Dodaj pliki]]]')"
                                @click.prevent="uploader.openDialog()"
                            />

                            <ActionButton v-show="showDeleteButton" variant="danger" icon="far fa-xmark" :text="$t(`[[[Usuń${selected.length > 1 ? ' zaznaczone' : ''}]]]`)" @click.prevent="deleteModalRef.showModal(selected)" />
                            <ActionButton v-show="showMoveButton" variant="secondary" icon="far fa-arrows-up-down-left-right" :text="$t(`[[[Przenieś${selected.length > 1 ? ' zaznaczone' : ''}]]]`)" @click.prevent="moveModalRef.showModal(selected)" />
                            <div class="mx-auto" />
                            <ActionButton v-show="showCropperToolbar" variant="secondary" icon="far fa-crop" :text="$t(`[[[Wytnij]]]`)" @click.prevent="cropImage" />
                            <ActionButton v-show="showCropperToolbar" variant="secondary" icon="far fa-expand-arrows-alt" :text="$t(`[[[Zmień rozmiar]]]`)" @click="resizeModalRef.showModal(currentEdit)" />
                            <ActionButton v-show="showCropperToolbar" variant="light" icon="far fa-ban" :text="$t(`[[[Anuluj]]]`)" @click.prevent="cropper = false" />
                        </ActionBar>
                    </template>
                    <template #end>
                        <DataSorting v-show="!cropper && template !== 'list'" class="ms-auto me-2 mb-0" :pager="pager" :options="sortingOptions" @change="loadData()" />
                        <IdeoDropdown v-if="!cropper" right no-caret :class="{ 'ms-auto': template === 'list' }">
                            <template #button-content>
                                <i v-if="template == 'thumbs'" class="icon far fa-th" />
                                <i v-if="template == 'big-thumbs'" class="icon far fa-th-list" />
                                <i v-if="template == 'list'" class="icon far fa-list" />
                            </template>
                            <IdeoDropdownItemButton :active="template == 'thumbs'" @click.prevent="changeTemplate('thumbs')">
                                <i class="icon far fa-th" />
                                <span>{{ $t('[[[Mały układ siatki]]]') }}</span>
                            </IdeoDropdownItemButton>
                            <IdeoDropdownItemButton :active="template == 'big-thumbs'" @click.prevent="changeTemplate('big-thumbs')">
                                <i class="icon far fa-border-all" />
                                <span>{{ $t('[[[Duży układ siatki]]]') }}</span>
                            </IdeoDropdownItemButton>
                            <IdeoDropdownItemButton :active="template == 'list'" @click.prevent="changeTemplate('list')">
                                <i class="icon far fa-list" />
                                <span>{{ $t('[[[Układ listy]]]') }}</span>
                            </IdeoDropdownItemButton>
                        </IdeoDropdown>
                    </template>
                </IdeoPanel>
            </template>

            <!-- Default -->
            <template #default>
                <div v-if="!(checked || upload.length)" v-show="false">
                    <PortalTarget name="upload-wrapper" />
                </div>
                <!-- Breadcrumbs -->
                <div class="d-flex justify-content-between">
                    <label v-if="resources.length || (selected.length && filemanagerData.scope === 'selected')" class="d-flex align-items-center me-4 mb-4">
                        <input
                            id="checkboxSelectAll"
                            ref="selectAllRef"
                            class="form-check-input me-2 mt-0 pointer"
                            type="checkbox"
                            :checked="filemanagerData.scope === 'selected' || (selected.length === resources.length && !!resources.length)"
                            :indeterminate="filemanagerData.scope !== 'selected' && selected.length > 0 && selected.length < resources.length"
                            :disabled="filemanagerData.scope === 'selected' && selected.length === 0"
                            @click="selectAll"
                        >

                        <span class="pointer">{{ $t('[[[Zaznacz wszystkie]]]') }}</span>
                    </label>

                    <component
                        :is="modal ? 'nav' : 'portal'"
                        v-show="!(modal && cropper)"
                        :id="modal ? 'breadcrumbs': null"
                        :to="modal ? null : 'breadcrumbs'"
                        aria-label="breadcrumb"
                    >
                        <span
                            class="link"
                            :class="{ active: breadcrumbs.length === 0 }"
                            @click.prevent="changeFolder(filemanagerData.scope, null)"
                        >
                            {{ $t(`[[[${getCurrentScope().label}]]]`) }}
                        </span>

                        <span
                            v-for="(item, index) in visibleBreadcrumbs"
                            :key="item.id"
                            class="link"
                            :class="{ 'active': index === visibleBreadcrumbs.length - 1, 'separator': item.separator }"
                            @click.prevent="changeFolder(filemanagerData.scope, item.id)"
                        >
                            {{ item.label }}
                        </span>
                    </component>
                </div>

                <!-- Cropper or files -->
                <CCropper v-if="cropper" :selected="currentEdit" :enabled="cropper" @reload="loadData" @error="onImageError" />
                <DropZone
                    v-else
                    v-model="upload"
                    :enabled="filemanagerData.scope != 'selected' && permissions.canEdit"
                    :scope="filemanagerData.scope"
                    :folder="filemanagerData.folder"
                >
                    <template v-if="template === 'list'">
                        <ListView
                            :items="getListViewItems(filemanagerData.scope === 'selected' ? selected : resources)"
                            :pager="pager"
                            :row-click="listRowClick"
                            :preload="false"
                            :draggable="filemanagerData.scope === 'selected'"
                            @change="loadData"
                            @on-drag="(value: FileResource[]) => selected = value"
                        >
                            <template #row="{ item }">
                                <ListViewHeader :title="$t('[[[Nazwa]]]')" sort="Name">
                                    <div class="d-flex align-items-center" :class="{ 'text-primary': isActive(item, checked) }">
                                        <input
                                            :id="`input-checkbox-${item.id}`"
                                            class="form-check-input mt-0 me-2 pointer"
                                            :class="{
                                                'invisible': item.resourceType === 'previous-folder',
                                            }"
                                            type="checkbox"
                                            :checked="isActive(item)"
                                            @click.stop="selectItem(item, true, false, $event)"
                                        >

                                        <i :class="['fas fa-fw me-2', getFileOptions(item).classes]" />
                                        {{ item.name }}
                                    </div>
                                </ListViewHeader>
                                <ListViewItem :title="$t('[[[Rozmiar pliku]]]')">
                                    {{ item.contentLength ? $filters.filesize(item.contentLength) : '---' }}
                                </ListViewItem>
                                <ListViewItem :title="$t('[[[Typ pliku]]]')">
                                    {{ getFileOptions(item).text }}
                                </ListViewItem>
                                <ListViewItem :title="$t('[[[Data utworzenia]]]')" sort="DateCreatedUtc">
                                    <i class="far fa-clock" />
                                    {{ $filters.datetime(item.dateCreatedUtc) }}
                                </ListViewItem>
                                <ListViewItem :title="$t('[[[Data modyfikacji]]]')" sort="DateModifiedUtc">
                                    <i class="far fa-clock" />
                                    {{ $filters.datetime(item.dateModifiedUtc) }}
                                </ListViewItem>

                                <ListViewButtons width="50" icon="far fa-ellipsis-vertical">
                                    <ListViewButton
                                        v-for="option in getDropdownOptions(item)"
                                        :key="option.id"
                                        v-lightbox="{ inactive: !item.isImage }"
                                        :tag="option.id === 'download' ? 'a' : 'button'"
                                        v-bind="bindItem(option, item)"
                                        @click.stop="option.onClick?.()"
                                    >
                                        <i class="icon" :class="option.icon" />
                                        <span>{{ option.text }}</span>
                                    </ListViewButton>
                                </ListViewButtons>
                            </template>
                        </ListView>
                        <PaginationBar :pager="pager" space @change="loadData" />
                    </template>
                    <template v-else>
                        <p v-if="folders.length + files.length === 0 && !parentFolderId">
                            <template v-if="loading">
                                {{ $t('[[[Ładowanie elementów...]]]') }}
                            </template>
                            <template v-else>
                                {{ $t('[[[Nie znaleziono żadnych elementów]]]') }}
                            </template>
                        </p>

                        <!-- Folders -->
                        <CResourceList
                            v-if="folders.length || breadcrumbs?.length"
                            v-model="folders"
                            type="folders"
                            :selected="selected"
                            :scope="filemanagerData.scope"
                            class="mb-4 pb-3"
                            :template="template"
                            :media="props.media"
                            :prepend-items="prependFolderItems"
                            @change-folder="changeFolder"
                            @select-item="selectItem"
                        />

                        <!-- Files -->
                        <CResourceList
                            v-if="files.length"
                            v-model="files"
                            type="files"
                            :selected="selected"
                            :scope="filemanagerData.scope"
                            :template="template"
                            :media="props.media"
                            @select-item="selectItem"
                            @edit-item="editItem"
                        />

                        <div v-if="!loading && pager.getPageIndex() < pager.getTotalPages()" class="alert alert-secondary text-center pointer mt-3 mb-0" @click="loadMore()">
                            {{ $t('[[[pokaż więcej...]]]') }}
                        </div>
                    </template>
                </DropZone>
            </template>
        </DataCard>
    </div>
</template>

<style lang="scss" scoped>
.filemanager {
    --ideo-menu-item-space: 4px;
    --ideo-menu-icon-space: 20px;
}
</style>

<style lang="scss">
.filemanager {
    width: 100%;
    height: 100%;

    &-data-card {
        height: 100%;

        .card {
            height: 100%;

            &-body {
                height: 100%;
                overflow: auto;
            }
            .panel {
                &.center {
                    height: 100%;
                }
            }
        }
    }

    &-backdrop, &-modal {
        z-index: 10400 !important;
    }

    &-tree {
        padding: 0 0.75rem;
        margin-top: 1rem;
    }

    .dropzone {
        cursor: unset;
    }

    .data-sorting {
        .dropdown-btn {
            &:first-child {
                color: var(--bs-gray-500);
                border-color: var(--bs-gray-300) !important;
                background: var(--bs-white);

                span.far {
                    color: var(--bs-gray-800);
                }
            }
        }
    }
}

.app-modal.filemanager {
    .filemanager {
        & > div {
            height: 100%;
        }
    }
}

.filemanager-modal {
    #breadcrumbs {
        height: auto;
    }
}
</style>
